import { AI_AGENT_COSTS, AI_SURFACE_LABELS, type AiSurfaceKey } from './aiAgentCosts'

/**
 * Wave 8 · AI Action Certification Matrix.
 *
 * Every AI entry point exposed to the user must declare, in one row here, how
 * it satisfies the four wave-8 guardrails:
 *   1. cost      — a key in AI_AGENT_COSTS (structural ops are cost-tracked).
 *   2. permission — the workspace membership gate applied on the server.
 *   3. reviewBoundary — where the user reviews AI output before it hits
 *                       source-of-truth collections. Read-only entry points
 *                       declare 'READ_ONLY'; write entry points must go
 *                       through 'AI_PROPOSAL' (the ai_proposals pending-tree).
 *   4. auditEvent — the audit action written on success.
 *
 * The companion test (`aiEntryPoints.certification.test.ts`) asserts:
 *   - every AI_SURFACE_LABELS key has a matrix row,
 *   - every matrix row's cost key exists in AI_AGENT_COSTS,
 *   - every matrix row declares a callable name, a permission, a review
 *     boundary, and an audit event.
 *
 * Adding a new AI entry point without updating this table will fail CI.
 */

export type AiPermissionGate =
  | 'workspace.member' // resolveMemberRole
  | 'workspace.editor' // requireAtLeastEditor
  | 'workspace.admin'

export type AiReviewBoundary =
  | 'READ_ONLY'
  | 'AI_PROPOSAL'
  | 'REPORT_CACHE'

export type AiEntryPointCertification = {
  surface: AiSurfaceKey
  callableName: string
  costKey: keyof typeof AI_AGENT_COSTS
  permission: AiPermissionGate
  reviewBoundary: AiReviewBoundary
  auditEvent: string
}

export const AI_ENTRY_POINT_MATRIX: Record<AiSurfaceKey, AiEntryPointCertification> = {
  PROJECT_ARCHITECT: {
    surface: 'PROJECT_ARCHITECT',
    callableName: 'executeAIProposal',
    costKey: 'BIG_IDEA',
    permission: 'workspace.editor',
    reviewBoundary: 'AI_PROPOSAL',
    auditEvent: 'AI_PROPOSAL_EXECUTED',
  },
  RISK_SCANNER: {
    surface: 'RISK_SCANNER',
    callableName: 'scanWorkspaceRisk',
    costKey: 'RISK_SCANNER',
    permission: 'workspace.member',
    reviewBoundary: 'READ_ONLY',
    auditEvent: 'AI_RISK_SCANNER_RAN',
  },
  BUDGET_ADVISOR: {
    surface: 'BUDGET_ADVISOR',
    callableName: 'scanWorkspaceBudgetAdvisor',
    costKey: 'BUDGET_ADVISOR',
    permission: 'workspace.member',
    reviewBoundary: 'READ_ONLY',
    auditEvent: 'AI_BUDGET_ADVISOR_RAN',
  },
  PULSE_SUMMARY: {
    surface: 'PULSE_SUMMARY',
    callableName: 'generatePulseSummary',
    costKey: 'PULSE_SUMMARY',
    permission: 'workspace.member',
    reviewBoundary: 'REPORT_CACHE',
    auditEvent: 'AI_PULSE_SUMMARY_GENERATED',
  },
  GOVERNANCE_REVIEW: {
    surface: 'GOVERNANCE_REVIEW',
    callableName: 'createAIProposal',
    costKey: 'GOVERNANCE_REVIEW',
    permission: 'workspace.editor',
    reviewBoundary: 'AI_PROPOSAL',
    auditEvent: 'AI_PROPOSAL_CREATED',
  },
  REPORT_REPHRASE: {
    surface: 'REPORT_REPHRASE',
    callableName: 'rephraseDeterministicReport',
    costKey: 'REPORT_REPHRASE',
    permission: 'workspace.member',
    reviewBoundary: 'READ_ONLY',
    auditEvent: 'AI_REPORT_REPHRASED',
  },
  DIGEST_REPHRASE: {
    surface: 'DIGEST_REPHRASE',
    callableName: 'rephraseWorkspaceDigest',
    costKey: 'DIGEST_REPHRASE',
    permission: 'workspace.member',
    reviewBoundary: 'READ_ONLY',
    auditEvent: 'AI_DIGEST_REPHRASED',
  },
}

/** Lists every surface key registered in the matrix. */
export const listAiEntryPoints = (): AiEntryPointCertification[] =>
  (Object.keys(AI_ENTRY_POINT_MATRIX) as AiSurfaceKey[]).map((key) => AI_ENTRY_POINT_MATRIX[key])

/** Sanity helper used by cost-display UIs to resolve a surface's declared cost. */
export const aiEntryPointCost = (surface: AiSurfaceKey): number =>
  AI_AGENT_COSTS[AI_ENTRY_POINT_MATRIX[surface].costKey]

/** Sanity helper used by cost-display UIs to resolve a surface's label. */
export const aiEntryPointLabel = (surface: AiSurfaceKey): string =>
  AI_SURFACE_LABELS[surface].label

/**
 * R31 CH7.a: Chart AI credit purposes mirrored from functions-common/src/wallet.ts.
 */
export const AI_CHART_PURPOSES = {
  AI_CHART_DRAFT: { label: 'AI chart draft', fixedCredits: 2, requiredAction: 'ai.use', fallbackRole: 'EDITOR', surface: 'AI', settlement: 'FIXED' },
  AI_CHART_EXPLAIN: { label: 'AI chart explain', fixedCredits: 5, requiredAction: 'ai.use', fallbackRole: 'EDITOR', surface: 'AI', settlement: 'FIXED' },
  AI_CHART_SUGGEST: { label: 'AI chart suggest', fixedCredits: 10, requiredAction: 'ai.use', fallbackRole: 'EDITOR', surface: 'AI', settlement: 'FIXED' },
} as const
